import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Modal, Spinner } from 'react-bootstrap';
import { FaShieldAlt } from 'react-icons/fa';
import authApi from '../../api/authApi';
import PubSub from 'pubsub-js';

/**
 * Admin recovery tab on Setup → Users → user detail.
 * Reset MFA when the user lost their phone / backup codes.
 */
const UserMfaAdminTab = ({ recordId, onRefresh }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    setError('');
    try {
      const result = await authApi.mfaAdminStatus(recordId);
      if (result.success) {
        setStatus(result);
      } else {
        setError(result.errors || 'Unable to load MFA status');
        setStatus(null);
      }
    } catch (err) {
      setError(err.message || 'Unable to load MFA status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleReset = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await authApi.mfaAdminReset(recordId);
      if (result.success) {
        setShowConfirm(false);
        PubSub.publish('RECORD_SAVED_TOAST', {
          title: 'MFA reset',
          message:
            result.message ||
            'Authenticator cleared. User can sign in and enroll again.',
        });
        await loadStatus();
        if (typeof onRefresh === 'function') onRefresh();
      } else {
        setError(result.errors || 'Failed to reset MFA');
      }
    } catch (err) {
      setError(err.message || 'Failed to reset MFA');
    } finally {
      setBusy(false);
    }
  };

  const policyLabel = status?.policy
    ? String(status.policy).charAt(0).toUpperCase() +
      String(status.policy).slice(1).toLowerCase()
    : '—';

  return (
    <div className="p-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex align-items-start justify-content-between mb-3">
            <div>
              <h5 className="mb-1" style={{ color: '#002B5B' }}>
                <FaShieldAlt className="me-2" />
                MFA recovery
              </h5>
              <p className="text-muted small mb-0">
                Use this when the user lost their phone or backup codes and cannot
                complete login MFA. Clearing MFA does not change their password.
              </p>
            </div>
          </div>

          {loading && (
            <div className="text-muted py-2">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading MFA status…
            </div>
          )}

          {error && (
            <Alert variant="danger" className="py-2">
              {error}
            </Alert>
          )}

          {!loading && status && (
            <>
              <Alert variant="light" className="border small mb-3">
                <div>
                  <strong>Org policy:</strong> {policyLabel}
                </div>
                <div>
                  <strong>Status:</strong>{' '}
                  {status.enabled
                    ? `Enabled${
                        status.enrolledAt
                          ? ` on ${new Date(status.enrolledAt).toLocaleString()}`
                          : ''
                      } · ${status.unusedBackupCodes} backup code(s) left`
                    : 'Not enabled'}
                </div>
              </Alert>

              {status.policy === 'required' && (
                <Alert variant="info" className="small py-2">
                  After reset, the user must set up a new authenticator on their
                  next login (policy is Required).
                </Alert>
              )}

              <Button
                variant="outline-danger"
                disabled={busy || (!status.enabled && status.policy === 'off')}
                onClick={() => setShowConfirm(true)}
              >
                Reset MFA
              </Button>
              {!status.enabled && (
                <p className="text-muted small mt-2 mb-0">
                  MFA is not currently enabled for this user.
                  {status.policy !== 'off'
                    ? ' Reset still clears any pending setup secrets.'
                    : ''}
                </p>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showConfirm} onHide={() => !busy && setShowConfirm(false)} centered>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Reset MFA?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This removes the user&apos;s authenticator and all backup codes.
            They will sign in with password only, then enroll again if your org
            requires MFA.
          </p>
          <p className="text-muted small mb-0">
            Only use this for locked-out users who contacted you for help.
          </p>
          {error && (
            <Alert variant="danger" className="py-2 mt-3 mb-0">
              {error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => setShowConfirm(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" disabled={busy} onClick={handleReset}>
            {busy ? 'Resetting…' : 'Reset MFA'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserMfaAdminTab;
